const libPictView = require('pict-view');

/**
 * OrganizationAddressTab — a master/detail editor for an Organization's addresses, rendered as a tab in the
 * Organization read view's Split layout (the secondary form "to the right" of the record).
 *
 * OrganizationAddress is a 1:N child of Organization (its IDOrganization FK). This view lists the current
 * org's addresses; clicking a row opens that address's edit form in place (edit in the panel, no navigation
 * away), and "Add address" opens a blank form. Save POSTs (new) or PUTs (existing) the OrganizationAddress
 * and returns to the list. Self-contained (own REST + templates), like the other bespoke read tabs
 * (ChildListTab, MaterialSpecSetsTab). Conditional rendering is done with 0/1-length "slot" arrays, the
 * pattern pict-template supports (there is no inline block-if).
 */

// The address fields the form exposes, in order. `Wide` spans the full row.
const FIELDS =
[
	{ Key: 'LocationName', Label: 'Location Name', Wide: true },
	{ Key: 'LocationType', Label: 'Location Type' },
	{ Key: 'Phone', Label: 'Phone' },
	{ Key: 'Email', Label: 'Email' },
	{ Key: 'Address', Label: 'Address', Wide: true },
	{ Key: 'City', Label: 'City' },
	{ Key: 'State', Label: 'State' },
	{ Key: 'Zip', Label: 'ZIP Code' },
];

class OrganizationAddressTab extends libPictView
{
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get viewID() { return this.options.ViewIdentifier || this.Hash; }

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)))); }
	_postJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pPayload, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)))); }
	_putJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pPayload, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)))); }

	_parentID() { return (this.pict.AppData.OrganizationDetails || {}).IDOrganization; }
	_escape(pValue) { return String((pValue === null || typeof pValue === 'undefined') ? '' : pValue).replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[pChar]); }
	_str(pValue) { return (pValue === null || typeof pValue === 'undefined') ? '' : String(pValue).trim(); }

	async _fetchAddresses()
	{
		const tmpParent = this._parentID();
		if (!tmpParent) { return []; }
		try
		{
			// Meadow pluralizes an entity route naively (Entity + "s"), so OrganizationAddress -> OrganizationAddresss.
			const tmpRows = await this._getJSON(`OrganizationAddresss/FilteredTo/FBV~IDOrganization~EQ~${ tmpParent }~FSF~LocationName~ASC~0/0/100`);
			return Array.isArray(tmpRows) ? tmpRows : [];
		}
		catch (pError)
		{
			this.pict.log.warn(`OrganizationAddressTab: fetch failed`, { Error: pError && (pError.message || pError) });
			return null; // error signal
		}
	}

	// --- Render state (the render Record; conditional blocks are 0/1-length slot arrays) ---

	_listState(pRows, pFailed)
	{
		const tmpSaved = ![ undefined, null, '' ].includes(this._parentID());
		const tmpRows = (pRows || []).map((pRow) =>
		{
			const tmpName = this._str(pRow.LocationName) || this._str(pRow.Address) || `Address #${ pRow.IDOrganizationAddress }`;
			const tmpSub = [ this._str(pRow.City), this._str(pRow.State), this._str(pRow.Zip) ].filter(Boolean).join(', ');
			return { ViewID: this.viewID, ID: pRow.IDOrganizationAddress, Title: this._escape(tmpName), Sub: this._escape(tmpSub), Type: this._escape(this._str(pRow.LocationType)) };
		});
		const tmpShowEmpty = pFailed || (tmpRows.length === 0);
		const tmpMessage = pFailed ? 'Could not load addresses.' : (tmpSaved ? 'No addresses yet — add one.' : 'Save the organization before adding addresses.');
		return {
			ViewID: this.viewID,
			ListSlot: [ {
				ViewID: this.viewID,
				AddSlot: tmpSaved ? [ { ViewID: this.viewID } ] : [],
				EmptySlot: tmpShowEmpty ? [ { Message: tmpMessage } ] : [],
				Rows: tmpRows
			} ],
			EditSlot: []
		};
	}

	_editState(pRecord)
	{
		const tmpNew = !pRecord || !pRecord.IDOrganizationAddress;
		const tmpHeading = tmpNew ? 'New Address' : (this._str(pRecord.LocationName) || 'Edit Address');
		return {
			ViewID: this.viewID,
			ListSlot: [],
			EditSlot: [ {
				ViewID: this.viewID,
				Heading: this._escape(tmpHeading),
				Fields: FIELDS.map((pField) => ({ ViewID: this.viewID, Key: pField.Key, Label: pField.Label, WideClass: pField.Wide ? 'wide' : '', Value: this._escape(this._str((pRecord || {})[pField.Key])) }))
			} ]
		};
	}

	// The read view sets our DefaultDestinationAddress to the tab body and calls renderAsync() with no args;
	// internal re-renders (list <-> edit) call renderAsync() again and land back in that same panel.
	async renderAsync(pRenderable, pDestinationAddress, pData, fCallback)
	{
		if (!this._record) { const tmpRows = await this._fetchAddresses(); this._record = this._listState(tmpRows, tmpRows === null); }
		return super.renderAsync(pRenderable || this.options.DefaultRenderable, pDestinationAddress || this.options.DefaultDestinationAddress, this._record, fCallback);
	}

	async _showList()
	{
		const tmpRows = await this._fetchAddresses();
		this._record = this._listState(tmpRows, tmpRows === null);
		return this.renderAsync();
	}

	async edit(pID)
	{
		this._editID = pID;
		try
		{
			// Read-by-id uses the SINGULAR route (OrganizationAddress/:id) and returns the record directly.
			const tmpRecord = await this._getJSON(`OrganizationAddress/${ pID }`);
			this._record = this._editState((Array.isArray(tmpRecord) ? tmpRecord[0] : tmpRecord) || { IDOrganizationAddress: pID });
		}
		catch (pError) { this._toast(`Could not load address: ${ pError && (pError.message || pError) }`, 'error'); return; }
		return this.renderAsync();
	}

	add() { this._editID = null; this._record = this._editState(null); return this.renderAsync(); }
	cancel() { return this._showList(); }

	async save()
	{
		const tmpPayload = {};
		FIELDS.forEach((pField) => { tmpPayload[pField.Key] = this.pict.ContentAssignment.readContent(`#OAT-${ this.viewID }-${ pField.Key }`) || ''; });
		try
		{
			if (this._editID)
			{
				tmpPayload.IDOrganizationAddress = this._editID;
				await this._putJSON('OrganizationAddress', tmpPayload);
			}
			else
			{
				tmpPayload.IDOrganization = this._parentID();
				await this._postJSON('OrganizationAddress', tmpPayload);
			}
			this._toast('Address saved.', 'success');
		}
		catch (pError) { this._toast(`Could not save: ${ pError && (pError.message || pError) }`, 'error'); return; }
		return this._showList();
	}

	_toast(pMessage, pType)
	{
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

const _DEFAULT_CONFIGURATION =
{
	ViewIdentifier: 'OrganizationAddresses',
	DefaultRenderable: 'OAT-Renderable',
	DefaultDestinationAddress: '#OAT-Container',
	DefaultTemplateRecordAddress: false,
	AutoInitialize: false,
	AutoRender: false,
	AutoSolveWithApp: false,
	CSS: /*css*/`
		.oat { font-size: 0.92rem; }
		.oat-head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin: 0 0 0.85rem; }
		.oat-head h3 { margin: 0; font-size: 1rem; font-weight: 600; color: var(--theme-color-text-primary, #1f2733); }
		.oat-add { display: inline-flex; align-items: center; gap: 0.35rem; border: 1px solid var(--theme-color-brand-primary, #5650e6); background: var(--theme-color-brand-primary, #5650e6); color: #fff; border-radius: 8px; padding: 0.4rem 0.8rem; font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer; }
		.oat-add:hover { filter: brightness(1.05); }
		.oat-list { display: grid; gap: 0.5rem; }
		.oat-row { text-align: left; width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 10px; padding: 0.7rem 0.9rem; background: var(--theme-color-background-primary, #fff); cursor: pointer; transition: all 0.12s ease; font: inherit; }
		.oat-row:hover { border-color: var(--theme-color-brand-primary, #5650e6); background: var(--theme-color-background-selected, #eef0fb); }
		.oat-row-main { min-width: 0; }
		.oat-row-name { font-weight: 600; color: var(--theme-color-text-primary, #1f2733); }
		.oat-row-sub { display: block; font-size: 0.82rem; color: var(--theme-color-text-muted, #6b7686); margin-top: 0.1rem; }
		.oat-row-type { flex: 0 0 auto; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #45505f); background: var(--theme-color-background-tertiary, #eef0f4); border-radius: 999px; padding: 0.15rem 0.6rem; }
		.oat-row-type:empty { display: none; }
		.oat-row-edit { flex: 0 0 auto; font-size: 0.8rem; font-weight: 600; color: var(--theme-color-brand-primary, #5650e6); white-space: nowrap; }
		.oat-empty { color: var(--theme-color-text-muted, #6b7686); text-align: center; padding: 1.5rem 0; }
		.oat-form { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem 1rem; }
		.oat-field.wide { grid-column: 1 / -1; }
		.oat-field label { display: block; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); margin-bottom: 0.3rem; }
		.oat-field input { width: 100%; box-sizing: border-box; padding: 0.5rem 0.7rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #1f2733); font: inherit; font-size: 0.9rem; }
		.oat-field input:focus { outline: none; border-color: var(--theme-color-brand-primary, #5650e6); }
		.oat-actions { grid-column: 1 / -1; display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.4rem; }
		.oat-btn { border-radius: 8px; padding: 0.45rem 1rem; font: inherit; font-size: 0.88rem; font-weight: 600; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.oat-btn.primary { background: var(--theme-color-status-success, #1f9d55); border-color: var(--theme-color-status-success, #1f9d55); color: #fff; }
		.oat-btn:hover { filter: brightness(0.98); }
	`,
	CSSPriority: 500,

	Templates:
	[
		{
			Hash: 'OAT-Root',
			Template: /*html*/`
				<div class="oat" id="OAT-Container">
					{~TS:OAT-List:Record.ListSlot~}
					{~TS:OAT-Edit:Record.EditSlot~}
				</div>`
		},
		{
			Hash: 'OAT-List',
			Template: /*html*/`
				<div class="oat-head"><h3>Addresses</h3>{~TS:OAT-AddButton:Record.AddSlot~}</div>
				{~TS:OAT-Empty:Record.EmptySlot~}
				<div class="oat-list">{~TS:OAT-Row:Record.Rows~}</div>`
		},
		{
			Hash: 'OAT-AddButton',
			Template: /*html*/`<button type="button" class="oat-add" onclick="_Pict.views['{~D:Record.ViewID~}'].add()">{~I:Plus~} Add address</button>`
		},
		{
			Hash: 'OAT-Empty',
			Template: /*html*/`<p class="oat-empty">{~D:Record.Message~}</p>`
		},
		{
			Hash: 'OAT-Row',
			Template: /*html*/`<button type="button" class="oat-row" onclick="_Pict.views['{~D:Record.ViewID~}'].edit({~D:Record.ID~})"><span class="oat-row-main"><span class="oat-row-name">{~D:Record.Title~}</span><span class="oat-row-sub">{~D:Record.Sub~}</span></span><span class="oat-row-type">{~D:Record.Type~}</span><span class="oat-row-edit">Edit {~I:ChevronRight~}</span></button>`
		},
		{
			Hash: 'OAT-Edit',
			Template: /*html*/`
				<div class="oat-head"><h3>{~D:Record.Heading~}</h3></div>
				<div class="oat-form">
					{~TS:OAT-Field:Record.Fields~}
					<div class="oat-actions">
						<button type="button" class="oat-btn" onclick="_Pict.views['{~D:Record.ViewID~}'].cancel()">Cancel</button>
						<button type="button" class="oat-btn primary" onclick="_Pict.views['{~D:Record.ViewID~}'].save()">{~I:Save~} Save</button>
					</div>
				</div>`
		},
		{
			Hash: 'OAT-Field',
			Template: /*html*/`<div class="oat-field {~D:Record.WideClass~}"><label>{~D:Record.Label~}</label><input type="text" id="OAT-{~D:Record.ViewID~}-{~D:Record.Key~}" value="{~D:Record.Value~}" /></div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'OAT-Renderable', TemplateHash: 'OAT-Root', RenderMethod: 'replace' }
	]
};

module.exports = OrganizationAddressTab;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
